import { useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import { useNavigate } from '@tanstack/react-router';
import StepHeader from '@components/step-header/StepHeader';
import { Accordion, Alert } from '@trussworks/react-uswds';
import InternalLink from '@components/internal-link/InternalLink';
import LinkButton from '@components/link/linkButton';
import NewApplicationModal from '@pages/Home/components/modals/NewApplication';
import genericSecondaryModal from '@forms/components/modals/genericSecondaryModal';
import { useModal, useRequest, useUserRoleCapabilities } from '@stores';
import {
  activityRequirementsAccordion,
  commonRequirementsAccordion,
  currentNwpLinks,
  faqAccordion,
  permitInstructionsTooltips,
} from './permitFormInstructionsHelper';
import Tooltip from '@src/app-components/tooltip/tooltip';
import ExternalLink from '@src/app-components/external-link/ExternalLink';
import { RequestType, ResourceFiles, FileTypes } from '@src/utils/enums';

export const PermitFormInstructionsMetadata = {
  header: 'Request General Permit',
  sectionName: 'Instructions',
};

const PermitFormInstructions = connect(
  'doDownloadFile',
  'doResetReduxFormData',
  ({ doDownloadFile, doResetReduxFormData, stepNo }) => {
    const { openModal, openSecondaryModal } = useModal();
    const navigate = useNavigate();
    const updateSection = useRequest((state) => state.updateSection);
    const canCreateRequest = useUserRoleCapabilities((state) => state.roleCapabilities.createRequest);

    useEffect(() => {
      updateSection(stepNo, { valid: true, label: PermitFormInstructionsMetadata.sectionName });
    }, [updateSection, stepNo]);

    const handleLink = () => {
      const onSaveFunc = () => {
        doResetReduxFormData();
        sessionStorage.setItem('IPButtonSession', true);
        const IPButtonSession = sessionStorage.getItem('IPButtonSession');
        navigate({ to: '/' });
        IPButtonSession && openModal(NewApplicationModal);
        sessionStorage.removeItem('IPButtonSession');
      };
      openSecondaryModal(genericSecondaryModal, {
        title: 'Are You Sure?',
        msg: `You are about to leave this application to start a new Pre-application request. Your current ${RequestType.PermitApplication} request will remain accessible via the RRS dashboard.`,
        cancelText: 'Cancel',
        onSave: onSaveFunc,
      });
    };

    return (
      <>
        <StepHeader text='Instructions' />

        <p>
          This path provides an electronic option for applicants to complete and submit applications for{' '}
          <span className='text-bold'>Standard Permits, Letters of Permission, and General Permits</span>. This section
          will guide you through the process to ensure that a complete application is submitted.
        </p>

        <h4>Standard Permit (SP)</h4>
        <p>
          Standard Permits (SP) are a type of <span className='text-bold'>Individual Permit</span> typically used for
          projects that involve more than minimal impacts to{' '}
          <InternalLink
            title='Waters of the United States (WOTUS)'
            href='/home/jurisdiction'
            content='Waters of the United States (WOTUS)'
            openInNewTab
          />{' '}
          or that exceed thresholds in the General Permits. An SP can be used to authorize all types of impacts to
          WOTUS. An SP requires a public interest review, including publication of a public notice (generally lasting 15
          or 30 days), National Environmental Policy Act review, Section 404b(1) of the Clean Water Act analysis, and
          coordination with involved agencies, interested parties, and the general public.{' '}
        </p>

        <h4>Letter of Permission (LOP)</h4>
        <p>
          A Letter of Permission (LOP) is a type of <span className='text-bold'>Individual Permit</span> issued through
          an abbreviated processing procedure that includes coordination with federal and state fish and wildlife
          agencies. Letters of Permission are commonly used to authorize structures in Section 10 waters but in some
          cases can authorize discharges of fill material in{' '}
          <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' openInNewTab />. A Letter of Permission
          will be issued where procedures of 33 CFR 325.2(e)(1) have been followed.
        </p>

        <h4>General Permits (GPs)</h4>
        <p>
          General Permits are typically not tailored for individual applicants/projects but instead authorize activities
          that the U.S. Army Corps of Engineers (USACE) has determined to be substantially similar in nature (such as
          utility lines, road culverts, bank stabilization, etc.) and which cause minimal individual and cumulative
          adverse environmental impacts.
        </p>
        <p>There are three types of General Permits:</p>
        <ul>
          <li>Nationwide Permits (NWPs)</li>
          <li>Regional General Permits (RGPs)</li>
          <li>Programmatic General Permits (PGPs)</li>
        </ul>
        <p>
          NWPs are issued on a nationwide basis. Many NWPs require Pre-construction Notification (PCN) to the USACE
          before commencing those activities. This electronic form assists applicants in providing the information
          required for a complete PCN.
        </p>
        {currentNwpLinks.map((item, index) => (
          <p key={index}>
            <ExternalLink href={item.href} content={item.content} />
          </p>
        ))}
        <p>
          RGPs and PGPs authorize activities within a limited geographic area (such as a county or state) or a specific
          region (such as a group of contiguous states). This form may also be used in some districts to submit a PCN
          for RGPs and PGPs.
        </p>
        <hr className='margin-bottom-3' />

        <p className='text-bold margin-top-3'>
          By completing this form fully and accurately, you will be satisfying the requirements for a complete
          application for a Standard Permit or a Letter of Permission, and for a complete General Permit preconstruction
          notification in some Districts.
        </p>

        <Alert className='margin-bottom-3' heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
          <p className='text-bold'>
            The United States Army Corps of Engineers (USACE) strongly encourages pre-application coordination meetings
            prior to submitting applications for Standard Permits, or if you're unfamiliar with the Nationwide Permit
            program and/or local USACE District Regulatory procedures.
          </p>
          <p>
            Pre-application meetings provide the applicant with helpful information necessary in pursuing a complete
            application, including factors the USACE must consider in its permit decision making process.
            Pre-application meetings are most productive when the applicant has prepared a delineation of{' '}
            <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' openInNewTab />, developed information
            regarding impacts to WOTUS, developed a project purpose, considered alternatives (on and offsite) to reduce
            impacts to WOTUS, and considered a mitigation statement.{' '}
            <span className='text-bold'>
              Pre-application meetings improve the USACE and other state/federal agency's understanding of the proposed
              project and provides helpful information to the applicant regarding information requirements which reduces
              delays in application reviews and permit decisions.{' '}
            </span>
          </p>
          <LinkButton
            className='btn office-support-button margin-bottom-3'
            onClick={() => handleLink()}
            title={`Submit a ${RequestType.PreApplication} request`}
            content={`Submit a ${RequestType.PreApplication} request`}
            disabled={!canCreateRequest}
          />
        </Alert>

        <h4>Requirements for Department of the Army (DA) Permit Applications</h4>
        <p>
          The permit application must include a complete{' '}
          <span className='text-bold'>description of the proposed activity</span> including necessary{' '}
          <span className='text-bold'>drawings, sketches, or plans</span> sufficient for public notice (detailed
          engineering plans and specifications are not required); the{' '}
          <span className='text-bold'>
            location, purpose and need for the proposed activity; scheduling of the activity
          </span>
          ; the{' '}
          <span className='text-bold'>
            names and addresses of adjoining property owners; the location and dimensions of adjacent structures
          </span>
          ; and a{' '}
          <span className='text-bold'>
            list of authorizations required by other federal, interstate, state, or local agencies
          </span>{' '}
          for the work, including all approvals received or denials already made (33 CFR 325.1(d)(1)).
        </p>
        <p>
          In accordance with Nationwide Permit General Condition 32(b), specific information must be included in the PCN
          before it can be considered complete and before the applicant can proceed with the proposed activity. The
          USACE District will review the PCN to determine if it is complete within 30 calendar days of the date of
          receipt and, if the PCN is determined to be incomplete, notify the applicant within that 30-day period to
          request the additional information necessary to make the PCN complete. As a general rule, districts will
          request additional information necessary to make the PCN complete only once. However, if the applicant does
          not provide all the requested information, then the USACE District will notify the applicant that the PCN is
          still incomplete and the PCN review process will not commence until all of the requested information has been
          received by the USACE District.
        </p>
        <Alert heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
          <span className='text-bold'>
            The application must include all activities which the applicant plans to undertake which are reasonably
            related to the same project and for which a USACE permit would be required (33 CFR 325.1(d)(2)).
          </span>
          For example, a permit application for a marina may include dredging required for access as well as any fill
          associated with construction of the marina. Applicants should make every effort to disclose all
          known/reasonably foreseeable phases of the proposed project.{' '}
          <span className='text-bold'>
            The USACE project manager ultimately determines whether the information submitted meets the requirements for
            a complete application.
          </span>
        </Alert>

        <p className='margin-top-2 text-italic'>Select each of the topics below to learn more.</p>
        <Accordion
          bordered
          items={commonRequirementsAccordion(doDownloadFile)}
          multiselectable={true}
          className='padding-left-2 margin-bottom-3'
        />

        <h5>Requirements Based on the Type of Activity</h5>
        <p className='text-italic'>Select each of the activity types below to learn more.</p>
        <Accordion
          bordered
          items={activityRequirementsAccordion}
          multiselectable={true}
          className='padding-left-2 margin-bottom-3'
        />

        <h5>Frequently Asked Questions</h5>
        <p className='text-italic'>Select each of the questions below to learn more.</p>
        <Accordion bordered items={faqAccordion} multiselectable={true} className='padding-left-2' />

        <Alert heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
          Districts may have District-specific requirements for complete applications by regional conditions
          <Tooltip name='RegionalConditionsTooltip' content={permitInstructionsTooltips.RegionalConditions} />. Please
          consult the local District's regional conditions on
          <ExternalLink href='https://www.regulations.gov/search?filter=COE-2020-0002' content=' Regulations.gov' />
        </Alert>
        <div className='margin-top-2'>
          <LinkButton
            onClick={() => doDownloadFile(ResourceFiles.Reg101FreqUsedTerms, FileTypes.Resource)}
            title='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
            content='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
          />
        </div>
      </>
    );
  }
);

PermitFormInstructions.metadata = PermitFormInstructionsMetadata;

export default PermitFormInstructions;
